import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, TextField, InputAdornment,
  Menu, MenuItem, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Grid, FormControl,
  InputLabel, Select, Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon, MoreVert as MoreVertIcon, Add as AddIcon,
  PersonOff as RemoveIcon,
} from '@mui/icons-material';
import wagService, { WAGOperador, WAGAgencia } from '../../services/wag.service';

const PAPEIS = ['ADMIN', 'SUPERVISOR', 'OPERADOR'];
const papelColor: Record<string, 'error' | 'warning' | 'default'> = {
  ADMIN: 'error',
  SUPERVISOR: 'warning',
  OPERADOR: 'default',
};

const WAGOperadores: React.FC = () => {
  const [agencias, setAgencias] = useState<WAGAgencia[]>([]);
  const [agenciaId, setAgenciaId] = useState('');
  const [items, setItems] = useState<WAGOperador[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selected, setSelected] = useState<WAGOperador | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<{ userId: string; papel: string; balcaoId: string }>({ userId: '', papel: 'OPERADOR', balcaoId: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Carregar agências
  useEffect(() => {
    wagService.getAgencias().then(data => {
      setAgencias(data);
      if (data.length > 0) setAgenciaId(data[0].id);
    });
  }, []);

  const load = useCallback(async () => {
    if (!agenciaId) return;
    setLoading(true);
    setError(null);
    try {
      setItems(await wagService.getOperadores(agenciaId));
    } catch {
      setError('Erro ao carregar operadores');
    } finally {
      setLoading(false);
    }
  }, [agenciaId]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(i =>
    !search
    || (i.nomeUtilizador || '').toLowerCase().includes(search.toLowerCase())
    || (i.email || '').toLowerCase().includes(search.toLowerCase())
  );

  async function handleAdd() {
    setSaving(true);
    setSaveError(null);
    try {
      await wagService.addOperador(agenciaId, form);
      setAddOpen(false);
      setForm({ userId: '', papel: 'OPERADOR', balcaoId: '' });
      load();
    } catch (e: any) {
      setSaveError(e?.response?.data?.error || 'Erro ao adicionar operador');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove() {
    // Desactivar operador
    setAnchorEl(null);
    // TODO: endpoint de remoção (PUT estado=I)
    load();
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Operadores WAG</Typography>
          <Typography variant="body2" color="text.secondary">Utilizadores com acesso ao painel de atendimento</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSaveError(null); setAddOpen(true); }}
          sx={{ bgcolor: '#e94560', '&:hover': { bgcolor: '#c73350' } }}>
          Adicionar Operador
        </Button>
      </Box>

      {/* Selector de agência */}
      {agencias.length > 1 && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Agência</InputLabel>
            <Select value={agenciaId} label="Agência" onChange={e => setAgenciaId(e.target.value)}>
              {agencias.map(a => <MenuItem key={a.id} value={a.id}>{a.desig}</MenuItem>)}
            </Select>
          </FormControl>
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField fullWidth placeholder="Pesquisar por nome ou email…" value={search}
          onChange={e => setSearch(e.target.value)} size="small"
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell>Utilizador</TableCell>
              <TableCell>Papel</TableCell>
              <TableCell>Balcão fixo</TableCell>
              <TableCell>Desde</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acções</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                {agenciaId ? 'Nenhum operador encontrado' : 'Seleccione uma agência'}
              </TableCell></TableRow>
            ) : filtered.map(op => (
              <TableRow key={op.id} hover>
                <TableCell>
                  <Typography fontWeight={600}>{op.nomeUtilizador || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">{op.email}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={op.papel} size="small" color={papelColor[op.papel] || 'default'} />
                </TableCell>
                <TableCell>
                  {op.balcaoNumero ? `Balcão ${op.balcaoNumero} — ${op.balcaoDesig}` : '—'}
                </TableCell>
                <TableCell>{new Date(op.dtInicio).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}</TableCell>
                <TableCell>
                  <Chip label={op.estado === 'A' ? 'Activo' : 'Inactivo'} size="small"
                    color={op.estado === 'A' ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={e => { setSelected(op); setAnchorEl(e.currentTarget); }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleRemove} sx={{ color: 'error.main' }}>
          <RemoveIcon fontSize="small" sx={{ mr: 1 }} />Remover operador
        </MenuItem>
      </Menu>

      {/* Dialog adicionar operador */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Operador</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField label="ID do Utilizador Pagali *" fullWidth size="small" value={form.userId}
                onChange={e => setForm(p => ({ ...p, userId: e.target.value }))}
                helperText="UUID do utilizador em pg_user" />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Papel</InputLabel>
                <Select value={form.papel} label="Papel" onChange={e => setForm(p => ({ ...p, papel: e.target.value }))}>
                  {PAPEIS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAdd} disabled={saving || !form.userId}
            sx={{ bgcolor: '#e94560', '&:hover': { bgcolor: '#c73350' } }}>
            {saving ? 'A adicionar…' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WAGOperadores;
